import Vue from 'vue';
import addressToString from '@/utils/addressToString';
import axios from 'axios';

import { getCsmCommunicationCode } from '@/utils/csm-cookies';
import { SOLDOUT_GROUP, UNALLOCATED_GROUP } from '../consts';
import { commonGtmEvents } from '@/gtm/common-gtm-events';
import { PAYMENT_METHODS } from '@/constants/payment';
import { createOrdersSessionStorage } from '@/services/storage/orders-storage';
import { cartGtmEvents } from '@/gtm/cart-gtm-events';
import { typGtmEvents } from '@/gtm/typ-gtm-events';

let cancelReqDelivery;
let cancelReqContact;

const KEY = 'order-numbers';

export default {
  namespaced: true,

  state: () => ({
    updateProgress: false,
    version: '',
    productCount: 0,
    contact: {},
    shippingGroups: [],
    shippingChannels: [], // todo deprecated, use - channels,
    channels: [],
    deliveryInfo: {},
    paymentMethods: [],
    promoCodes: [],
    bonuses: {},
    subtotal: '',
    pickupStores: {},
    thirdpartyStores: {},
    subscribe: false,
    comment: '',
    cartId: '',
    isLoading: false,
    cartDetails: {},
  }),

  getters: {
    isLoading: state => state.isLoading,
    updateProgress: state => state.updateProgress,
    version: state => state.version,
    productCount: state => state.productCount,
    products: state => state.shippingGroups.reduce((acc, g) => [...acc, ...g.products], []),
    shippingGroups: state => state.shippingGroups,
    shippingChannels: state => state.shippingChannels,
    deliveryInfo: state => state.deliveryInfo,
    deliverySelectedDate: state => {
      if (!state.deliveryInfo?.interval) return;
      let selectedInterval;

      const date = state.deliveryInfo.interval.find((date) => {
        const timeInterval = date.time.find(time => time.selected);
        if (timeInterval) {
          selectedInterval = timeInterval
        }

        return !!timeInterval;
      });

      if (!date) return;

      return {
        dateFrom: date.dateFrom,
        dateTo: date.dateTo,
        dateId: date.id,
        interval: selectedInterval.value,
        intervalId: selectedInterval.id
      };
    },
    getDeliveryDateById: (state, getters) => (intervalId) => {
      if (!state.deliveryInfo?.interval) return;
      let selectedInterval;

      const timeId = intervalId || getters.deliverySelectedDate?.intervalId;

      const date = state.deliveryInfo.interval.find(({ time }) => {
        selectedInterval = time.find(({ id }) => id === timeId);
        return !!selectedInterval;
      });

      if (!date) return;

      return {
        dateFrom: date.dateFrom,
        dateTo: date.dateTo,
        dateId: date.id,
        interval: selectedInterval.value,
        intervalId: selectedInterval.id
      };
    },
    deliveryAddress: state => addressToString(state.deliveryInfo.address) || '',
    paymentMethods: state => state.paymentMethods,
    isOnlinePaySelected: state => state.paymentMethods.find(({ method, selected }) => method === PAYMENT_METHODS.cardOnline && selected),
    isFpsPaySelected: state => state.paymentMethods.find(({ method, selected }) => method === PAYMENT_METHODS.fps && selected),
    contact: state => state.contact,
    bonuses: state => state.bonuses,
    subtotal: state => state.subtotal,
    promoCodes: state => state.promoCodes || [],
    pickupStores: state => state.pickupStores || {},
    thirdpartyStores: state => state.thirdpartyStores || {},
  },

  actions: {
    async loadCart({ commit, dispatch }) {
      commit('setProgress');

      try {
        const cart = await this.$api.loadCart();

        await dispatch('setCart', cart);
        commit('setProgress', false);
      } catch (error) {
        commit('setCart');
        commit('setProgress', false);
      }
    },

    async loadCartAfterLogin({ commit, getters, dispatch }) {
      try {
        await dispatch('loadCart');
        await dispatch('enableUseBonuses');

        // сбрасываем деливери шип группы после логина, если не указан адрес
        if (getters.deliveryInfo?.address?.streetName) return;

        commit('setProgress');
        const products = getters.shippingGroups.reduce((res, { type, products }) => {
          if (type !== 'delivery') return res;
          res = res.concat(products);
          return res;
        }, []);

        if (products.length) await dispatch('resetShippingGroup', products);
      } catch (e) {
        console.error(e);
      } finally {
        commit('setProgress', false);
      }
    },

    setCart({ commit, dispatch, rootState }, cart) {
      commit('setCart', cart);
      if (rootState.route.name === 'Checkout')
        dispatch('fillPickupPoints');
    },

    addCsm(ctx, { skuId = '', price = '', discountPrice = '' } = {}) {
      return this.$api.addCsm({
        wareId: skuId,
        price,
        discountPrice,
      });
    },

    async changeProduct({dispatch}, { lineIds, productId, skuId, quantity, storeId, shippingGroupId } = {}) {
      try {
        await dispatch('add', {
          items: [{
            productId,
            skuId,
            quantity,
          }], storeId, shippingGroupId })
        await dispatch('remove', { lineIds } );
      } catch (e) {
        throw new Error(e);
      }
    },

    async changeQty({ dispatch }, { quantity, updatedQuantity, lineIds, productId, skuId, shippingGroupId} = {}) {
      const diff = updatedQuantity - quantity;
      if (diff === 0) return;
      if (diff > 0) {
        await dispatch('add', {
          items: [{
            productId,
            skuId,
            quantity: diff,
          }], shippingGroupId,
        });

      } else if (diff < 0) {
        await dispatch('remove', {lineIds: lineIds.slice(lineIds.length + diff, lineIds.length)});
      }
    },

    getShippingGroup({ getters }, { shippingGroupId, storeId } = {}) {
      if ( storeId ) {
        return {
          idStore: storeId,
          shippingId: shippingGroupId,
        }
      }

      // шиппинг группы кроме нераспределенной
      const existsGroups = getters.shippingGroups.filter(group => group.type !== UNALLOCATED_GROUP && group.type !== SOLDOUT_GROUP);

      if (shippingGroupId && shippingGroupId !== UNALLOCATED_GROUP) {
        return { idStore: undefined,  shippingId: shippingGroupId };
      } else if (!shippingGroupId && existsGroups.length === 1) { // если существует уже шиппинг группа
        const { id } = existsGroups[0];

        return { idStore: undefined,  shippingId: id };
      } else if (shippingGroupId === UNALLOCATED_GROUP) {
        return { idStore: undefined,  shippingId: null };
      }

      return {
        idStore: undefined,  shippingId: undefined
      }
    },

    async add({ dispatch, commit }, { items = [], storeId, shippingGroupId } = {}) {
      try {
        commit('setProgress');
        const {
          idStore,
          shippingId,
        } = await dispatch('getShippingGroup', { shippingGroupId, storeId });

        const cart = await this.$api.addToCart({ items, storeId: idStore, shippingGroupId: shippingId });
        await dispatch('setCart', cart);
      } catch (e) {
        if (e?.response?.status === 404) {
          // SUCCESS
          await dispatch('loadCart');
          return;
        }
        throw new Error(e);
      } finally {
        commit('setProgress', false);
      }
    },

    async remove({ dispatch, commit }, { lineIds = [] } = {}) {
      try {
        commit('setProgress');
        const cart = await this.$api.removeFromCart({ lineIds });
        await dispatch('setCart', cart);
      } catch (e) {
        console.error(e);
      } finally {
        commit('setProgress', false);
      }
    },

    fillPickupPoints({ state, commit }) {
      // заполняет все точки самовывоза, которые используются в корзине
      const ids = {
        pickup: [],
        thirdparty: [],
      };
      state.shippingGroups.forEach(group => {
        if (group.type === 'pickup' && !state.pickupStores[group.storeId]) ids.pickup.push(group.storeId);
        if (group.type === 'thirdparty' && !state.thirdpartyStores[group.thirdPartyId]) ids.thirdparty.push(group.thirdPartyId);
      });

      if (ids.pickup.length) {
        Promise.all(ids.pickup.map(id => this.$api.getStore(id)))
          .then(stores => {
            stores.forEach(store => {
              commit('addPickupStore', store);
            });
          });
      }

      if (ids.thirdparty.length) {
        this.$api.getThirdpartyStores(ids.thirdparty)
          .then(thirdparty => {
            thirdparty.forEach(store => {
              commit('addThirdpartyStore', store);
            });
          });
      }
    },

    async setShippingGroup({ dispatch, commit }, { type, value, lineIds }) {
      // value - storeId
      try {
        commit('setLoadingStatus', true);
        commit('setProgress');
        const cart = await this.$api.setShippingGroup({ type, value, lineIds });
        await dispatch('setCart', cart);
        dispatch('saveComment'); // коммент можно указать, только с выбранной шип группой
      } catch (e) {
        console.error(e);
      } finally {
        commit('setLoadingStatus', false);
        commit('setProgress', false);
      }
    },

    async resetShippingGroup({ dispatch, commit }, products = []) { // переводит в unallocated
      try {
        await commit('setLoadingStatus', true);
        await dispatch('add', {
          items: products.map(({ id, skuId, quantity }) => ({
            productId: id,
            skuId,
            quantity,
          })),
          shippingGroupId: UNALLOCATED_GROUP, // чтоб не добавлял в уже существующую шипинггруппу
        })
        await dispatch('remove', { lineIds: products.map(({ lineIds = [] } = {}) => lineIds).flat() });
      } catch (e) {
        throw new Error(e);
      } finally {
        commit('setLoadingStatus', false);
      }
    },

    async saveDelivery({ commit }, form = {}) {
      if (cancelReqDelivery) {
        cancelReqDelivery();
      }

      try {
        commit('setProgress');
        const { request, cancel } = this.$api.saveDelivery(form);

        cancelReqDelivery = cancel;
        const cart = await request;

        commit('setCart', cart)
      } catch(e) {
        if(!axios.isCancel(e)) {
          console.error(e);
        }
      } finally {
        cancelReqDelivery = null;
        commit('setProgress', false);
      }
    },

    async saveContact({ dispatch, commit }, form = {}) {
      if (cancelReqContact) {
        cancelReqContact();
      }

      try {
        commit('setProgress');
        const { request, cancel } = this.$api.saveContact(form);
        cancelReqContact = cancel;

        dispatch('setCart', await request)
      } catch(e) {
        if(!axios.isCancel(e)) {
          console.error(e);
        }
      } finally {
        cancelReqContact = null;
        commit('setProgress', false);
      }
    },

    saveComment({ dispatch, commit, state }, { comment } = {}) {
      commit('setProgress');
      return this.$api.saveComment({ comment: comment ?? state.comment })
        .then(cart => dispatch('setCart', cart))
        .finally(() => {
          commit('setProgress', false);
        })
    },

    async setPaymentMethod({ dispatch, commit }, method = '') {
      try {
        commit('setLoadingStatus', true);
        const cart = await this.$api.setPaymentMethod(method);
        await dispatch('setCart', cart);
      } catch (e) {
        console.error(e)
      } finally {
        commit('setProgress', false);
        commit('setLoadingStatus', false);
      }
    },

    setPromo({ dispatch, commit }, code) {
      return this.$api.setPromo(code)
        .then(cart => dispatch('setCart', cart))
        .finally(() => {
          commit('setProgress', false);
        })
    },

    deletePromo({ dispatch, commit }, code) {
      return this.$api.deletePromo(code)
        .then(cart => dispatch('setCart', cart))
        .finally(() => {
          commit('setProgress', false);
        })
    },

    async applyBonuses({ dispatch, commit }, apply) {
      commit('setProgress');
      const cart = await this.$api.applyBonuses(apply)
      await dispatch('setCart', cart)
      commit('setProgress', false);
    },

    async enableUseBonuses({ dispatch, rootGetters, getters }) {
      if(!getters['cart/bonuses']?.applied && rootGetters['user/loggedIn']) {
        if (rootGetters['user/bonuses'] > 0) {
          await dispatch('applyBonuses', true);
        } else {
          await dispatch('applyBonuses', false);
        }
      }
    },

    async trackCartSubmit({ rootState, getters, state }, { orderNumbers, cartId }) {
      try {
        const orders = await this.$api.getOrderDetails({ orderNumbers });

        await cartGtmEvents.successSubmitOrder({
          cartId,
          orders,
          subtotal: getters.subtotal,
          discounts: state.cartDetails?.discounts?.discounts,
        });

        return commonGtmEvents.viewThanksPageAll({
          orders,
          cartId,
          accountId: rootState.user.account?.id,
        });
      } catch (error) {
        console.error(error);
      }
    },

    saveToStorageOrderNumbers(ctx, orderNumbers = []) {
      if (localStorage.getItem(KEY)) {
        localStorage.removeItem(KEY);
      }

      localStorage.setItem(KEY, orderNumbers);
    },

    getFromStorageOrderNumbers() {
      const orderNumber = localStorage.getItem(KEY);

      if (!orderNumber) {
        return [];
      }

      return orderNumber.split(',');
    },

    clearAllFromStorageOrderNumbers() {
      localStorage.removeItem(KEY);
    },

    async goPayMostExpensiveOrder({ dispatch }, orderNumbers = []) {
      const details = await this.$api.getOrderDetails({ orderNumbers });
      const sortedOrders = details
        .sort((orderA, orderB) => parseFloat(orderB.subtotal, 10) - parseFloat(orderA.subtotal, 10));
      const mostExpensiveOrder = sortedOrders[0];

      return dispatch('goToPayOrder', mostExpensiveOrder.orderNumber)
    },

    async submit({ dispatch, getters }) {
      try {
        const { orderNumbers = [], cartId } = await this.$api.checkout({ communicationCode: getCsmCommunicationCode() });

        if (orderNumbers.length) {
          await dispatch('trackCartSubmit', { orderNumbers, cartId }); // ждем, пока все данные уйдут в gtm
          await dispatch('saveToStorageOrderNumbers', orderNumbers);
        }

        //  для онлайн оплаты
        if (getters.isOnlinePaySelected) {
          // если несколько отдельных заказов
          // переводим на оплату самого дорогого
          if (orderNumbers.length > 1) {
            await dispatch('goPayMostExpensiveOrder', orderNumbers);
          } else {
            await dispatch('goToPayOrder', orderNumbers[0]);
          }

          return;
        }

        if (getters.isFpsPaySelected) {
          await dispatch('getPaymentFpsQr', { orderNumbers });
        }

        const url = `/thankyoupage?orders=${orderNumbers.join()}`;
        location.assign(url);
      } catch (error) {
        dispatch('showPayError', error);
      }
    },

    async getPaymentFpsQr(ctx, { orderNumbers }) {
      const ordersStorage = createOrdersSessionStorage();
      const promises = orderNumbers.map(orderNumber => this.$api.getPaymentFpsQr({ orderNumber }));

      const responses = await Promise.allSettled(promises)
        .then(results => results.map(({ value }) => value));

      ordersStorage.ordersWithFps = responses
        .filter(i => !!i)
        .reduce((res, item) => {
          res[item.orderNumber] = item;
          return res;
        }, {});

      return ordersStorage.ordersWithFps;
    },

    goToPayOrder(ctx, orderNumber) {
      typGtmEvents.payOnline({ orderNumber });
      return this.$api.getPaymentLink({ orderNumber })
        .then(({ url }) => {
          location.assign(url);
        })
        .catch((error) => {
          console.error(error);
          const url = `/thankyoupage?orders=${orderNumber}`;
          location.assign(url);
        });
    },

    showPayError({ dispatch }, error) {
      console.error(error)
      dispatch('notifications/create', {
        title: 'Ошибка',
        message: 'Не удалось оплатить заказ. Повторите запрос позже.'
      }, { root: true });
    }
  },

  mutations: {
    setCart: (state, {
      version = '',
      productCount = 0,
      contact = {},
      comment,
      shippingGroups = [],
      shippingChannels = [],
      deliveryInfo = {},
      paymentMethods = [],
      promoCodes = [],
      bonuses = {},
      subtotal = '',
      id = '',
      cartDetails = {},
      channels = [],
    } = {}) => {
      // Перекрывать дефолтом обязательно, тк может вернуть все поля null
      state.version = version || '';
      state.productCount = productCount || 0;

      const phone = contact.phone || '';

      state.contact = { ...contact, phone: phone.replace(/\D/g,'') } || {};
      state.comment = (comment ?? state.comment) || '';
      state.shippingGroups = shippingGroups || [];
      state.shippingChannels = shippingChannels || [];
      // выкидываем streetKind, апи сами определяют что есть что
      const { address, ...restDeliveryInfo } = deliveryInfo || {};
      // eslint-disable-next-line no-unused-vars
      const { streetKind, ...restAddress } = address || {};
      state.deliveryInfo = { address: { ...restAddress }, ...restDeliveryInfo };
      state.paymentMethods = paymentMethods || [];
      state.bonuses = bonuses || {};
      state.promoCodes = promoCodes || [];
      state.subtotal = subtotal || '';
      state.cartId = id;
      state.cartDetails = cartDetails || {};
      state.channels = channels || [];
    },

    setCartVersion(state, { version }) {
      state.version = version || '';
    },

    setProgress: (state, progress = true) => {
      state.updateProgress = progress;
    },

    addPickupStore(state, store) {
      Vue.set(state.pickupStores, store.id, store);
    },

    addThirdpartyStore(state, store) {
      Vue.set(state.thirdpartyStores, store.id, store);
    },

    subscribe(state, subscribe = true) {
      state.subscribe = subscribe;
    },

    setLoadingStatus(state, value = false) {
      state.isLoading = value;
    },

    setComment: (state, comment) => state.comment = comment,
  },
};
